import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { fmt } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Save, Download, AlertTriangle, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function CuadreWallet({ embedded = false }) {
  const { isAdmin } = useAuth();
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({ real_wallet_usdt: "", real_cash_usd: "", note: "" });

  const load = async () => {
    const today = new Date();
    const [s, h] = await Promise.all([
      api.get("/major/monthly-summary", { params: { month: today.getMonth() + 1, year: today.getFullYear() } }),
      api.get("/reconciliations"),
    ]);
    setSummary(s.data); setHistory(h.data);
  };
  useEffect(() => { load(); }, []);

  const usdtDiff = (parseFloat(form.real_wallet_usdt) || 0) - (summary?.available_usdt_balance || 0);
  const usdDiff = (parseFloat(form.real_cash_usd) || 0) - (summary?.available_cash_usd || 0);

  const save = async () => {
    if (!form.real_wallet_usdt || !form.real_cash_usd) { toast.error("Ingresa ambos balances reales"); return; }
    try {
      await api.post("/reconciliations", {
        real_wallet_usdt_balance: parseFloat(form.real_wallet_usdt),
        real_physical_cash_usd: parseFloat(form.real_cash_usd),
        note: form.note,
      });
      toast.success("Cuadre guardado");
      setForm({ real_wallet_usdt: "", real_cash_usd: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("token") || "";
    const res = await fetch(`${API}/reports/reconciliations`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const blob = await res.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `cuadres.csv`; a.click(); URL.revokeObjectURL(url);
  };

  if (!summary) return <div className="text-slate-500">Cargando...</div>;

  return (
    <div className="space-y-6" data-testid="cuadre-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          {!embedded && <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Cuadre Wallet / Caja</h1>}
          <p className="text-slate-500 mt-1">Compara el sistema contra los balances físicos reales</p>
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="export-recon"><Download size={16} className="mr-2" /> Exportar</Button>
      </div>

      {isAdmin && (
        <Card className="p-6 border border-slate-200 rounded-xl bg-white">
          <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider mb-4">Nuevo cuadre</h3>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-slate-700">USDT (Wallet)</h4>
              <div className="bg-slate-50 rounded-lg p-3 text-sm font-mono tabular-nums">
                <div className="flex justify-between"><span className="font-sans text-slate-600">Sistema</span><span>{fmt.usdt(summary.available_usdt_balance)} USDT</span></div>
              </div>
              <Label>Balance real en wallet (USDT)</Label>
              <Input type="number" step="0.01" value={form.real_wallet_usdt} onChange={(e) => setForm({ ...form, real_wallet_usdt: e.target.value })} data-testid="real-usdt" className="font-mono" />
              {form.real_wallet_usdt && (
                <div className={`p-3 rounded-lg text-sm font-mono tabular-nums ${Math.abs(usdtDiff) < 0.01 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                  Diferencia: {usdtDiff > 0 ? "+" : ""}{fmt.usdt(usdtDiff)} USDT
                </div>
              )}
            </div>
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-slate-700">USD efectivo (Caja)</h4>
              <div className="bg-slate-50 rounded-lg p-3 text-sm font-mono tabular-nums">
                <div className="flex justify-between"><span className="font-sans text-slate-600">Sistema</span><span>{fmt.usdt(summary.available_cash_usd)} USD</span></div>
              </div>
              <Label>Efectivo físico contado (USD)</Label>
              <Input type="number" step="0.01" value={form.real_cash_usd} onChange={(e) => setForm({ ...form, real_cash_usd: e.target.value })} data-testid="real-cash" className="font-mono" />
              {form.real_cash_usd && (
                <div className={`p-3 rounded-lg text-sm font-mono tabular-nums ${Math.abs(usdDiff) < 0.01 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                  Diferencia: {usdDiff > 0 ? "+" : ""}{fmt.usdt(usdDiff)} USD
                </div>
              )}
            </div>
          </div>
          <div className="mt-4">
            <Label>Nota</Label>
            <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Observaciones del cuadre" data-testid="recon-note" />
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={save} className="bg-slate-900" data-testid="save-recon"><Save size={16} className="mr-2" /> Guardar cuadre</Button>
          </div>
        </Card>
      )}

      <Card className="border border-slate-200 rounded-xl bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">Historial de cuadres</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="recon-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Fecha</th>
                <th className="px-4 py-3 text-right font-semibold">USDT sis.</th>
                <th className="px-4 py-3 text-right font-semibold">USDT wallet</th>
                <th className="px-4 py-3 text-right font-semibold">Dif. USDT</th>
                <th className="px-4 py-3 text-right font-semibold">USD sis.</th>
                <th className="px-4 py-3 text-right font-semibold">USD caja</th>
                <th className="px-4 py-3 text-right font-semibold">Dif. USD</th>
                <th className="px-4 py-3 text-left font-semibold">Estado</th>
                <th className="px-4 py-3 text-left font-semibold">Usuario</th>
                <th className="px-4 py-3 text-left font-semibold">Nota</th>
              </tr>
            </thead>
            <tbody className="font-mono tabular-nums">
              {history.length === 0 && (<tr><td colSpan={10} className="text-center py-8 text-slate-400">Sin cuadres registrados</td></tr>)}
              {history.map((h) => (
                <tr key={h.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 text-slate-600">{fmt.date(h.date || h.created_at)}</td>
                  <td className="px-4 py-3 text-right">{fmt.usdt(h.system_usdt_balance)}</td>
                  <td className="px-4 py-3 text-right">{fmt.usdt(h.real_wallet_usdt_balance)}</td>
                  <td className={`px-4 py-3 text-right ${Math.abs(h.usdt_difference) < 0.01 ? "text-emerald-600" : "text-rose-600"}`}>{h.usdt_difference > 0 ? "+" : ""}{fmt.usdt(h.usdt_difference)}</td>
                  <td className="px-4 py-3 text-right">{fmt.usdt(h.system_available_cash_usd)}</td>
                  <td className="px-4 py-3 text-right">{fmt.usdt(h.real_physical_cash_usd)}</td>
                  <td className={`px-4 py-3 text-right ${Math.abs(h.usd_difference) < 0.01 ? "text-emerald-600" : "text-rose-600"}`}>{h.usd_difference > 0 ? "+" : ""}{fmt.usdt(h.usd_difference)}</td>
                  <td className="px-4 py-3">
                    <Badge className={h.status === "cuadrado" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}>
                      {h.status === "cuadrado" ? <><CheckCircle size={12} className="inline mr-1" /> Cuadrado</> : <><AlertTriangle size={12} className="inline mr-1" /> Diferencia</>}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 font-sans text-slate-600">{h.created_by_name}</td>
                  <td className="px-4 py-3 font-sans text-slate-600 max-w-xs whitespace-normal break-words">{h.note || "Sin nota"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
